import { ValidationConfig } from '../../../../types';

type Question<O extends Option<C>, C extends Checkpoint> = {
  id: number;
  options: O[];
  nextQuestionId: number;
  checkpoint?: C;
  validationConfig?: ValidationConfig;
};

interface Option<C extends Checkpoint> {
  id: number;
  nextQuestionId: number;
  checkpoint?: C;
}

interface Checkpoint {
  id: number;
  isCompletionCheckpoint: boolean;
  reminderBaseDateQuestionId: number;
}

interface Response {
  questionId: number;
  selectedOptionIds: number[];
  skipped: boolean;
  createdAt: number;
}

interface Phase {
  completedAt: number;
  checkpoint: Checkpoint;
}

type Step<
  Q extends Question<Option<C>, C>,
  C extends Checkpoint,
  R extends Response
> = {
  question: Q;
  response: R;
  checkpoint: C;
  stopAtCheckpoint?: boolean;
};

function getNextStepFromLastStep<
  T extends Question<Option<C>, C>,
  C extends Checkpoint,
  R extends Response
>(
  lastStep: Step<T, C, R>,
  allQuestions: T[],
  defaultFinalCheckpoint: C
): Step<T, C, R> {
  if (!lastStep.response || !lastStep.question) {
    return null;
  }

  // The route of a survey can only be changed by questions that have single responses,
  // because multiple responses can lead to multiple paths which is not supported,
  // and so we only care about grabbing the selected option if there is only one selected option.
  let singleSelectedOption: Option<C>;
  if (lastStep.response.selectedOptionIds?.length === 1) {
    singleSelectedOption = lastStep.question.options?.find(
      o => o.id === lastStep.response.selectedOptionIds[0]
    );
  }

  const nextCheckpoint: C =
    singleSelectedOption?.checkpoint || lastStep.question.checkpoint;

  // If checkpoint is a completion checkpoint then there shouldn't be a next question
  if (nextCheckpoint?.isCompletionCheckpoint) {
    return {
      question: null,
      checkpoint: nextCheckpoint,
      response: null,
    };
  }

  const nextQuestion = getNextQuestion(
    lastStep.question,
    singleSelectedOption,
    allQuestions
  );

  // If there isn't a next question and no specified checkpoint then we should use the default final checkpoint
  if (!nextQuestion && !nextCheckpoint) {
    return {
      question: null,
      checkpoint: defaultFinalCheckpoint,
      response: null,
    };
  }

  return {
    question: nextQuestion,
    checkpoint: nextCheckpoint,
    response: null,
  };
}

function getNextQuestion<
  Q extends Question<Option<C>, C>,
  C extends Checkpoint
>(question: Q, singleSelectedOption: Option<C>, allQuestions: Q[]): Q {
  if (singleSelectedOption?.nextQuestionId) {
    return allQuestions.find(q => q.id === singleSelectedOption.nextQuestionId);
  } else if (question.nextQuestionId) {
    return allQuestions.find(q => q.id === question.nextQuestionId);
  } else {
    // get the next question based on sort order
    const index = allQuestions.findIndex(q => q.id === question.id);
    if (index + 1 > allQuestions.length) {
      return null;
    }
    return allQuestions[index + 1];
  }
}

function getLastQuestionAnswered<
  Q extends Question<Option<C>, C>,
  C extends Checkpoint,
  R extends Response
>(validSteps: Step<Q, C, R>[]): Q {
  for (let i = validSteps.length - 1; i >= 0; i--) {
    const step = validSteps[i];
    if (step.response && step.question) {
      return step.question;
    }
  }
}

function getValidAndInvalidResponses<
  Q extends Question<Option<C>, C>,
  C extends Checkpoint,
  R extends Response
>(
  validSteps: Step<Q, C, R>[],
  allQuestionResponses: R[]
): { invalid: R[]; valid: R[] } {
  const validQuestionsMap = validSteps.reduce((acc, q) => {
    if (q.response) {
      acc[q.question?.id] = true;
    }
    return acc;
  }, {} as Record<number, true>);
  const valid = allQuestionResponses.filter(
    r => validQuestionsMap[r.questionId]
  );
  const invalid = allQuestionResponses.filter(
    r => !validQuestionsMap[r.questionId]
  );

  return { valid, invalid };
}

function getJumpedResponses<
  Q extends Question<Option<C>, C>,
  C extends Checkpoint,
  R extends Response
>(validSteps: Step<Q, C, R>[]): R[] {
  if (!validSteps?.length) {
    return [];
  }

  const lastStep = validSteps[validSteps.length - 1];

  const responsesToBeJumpped: R[] = [];
  let hasGottenToNextQuestion = false;
  validSteps.forEach(step => {
    if (step !== lastStep && step.question?.id === lastStep.question?.id) {
      hasGottenToNextQuestion = true;
    }
    if (hasGottenToNextQuestion) {
      responsesToBeJumpped.push(step.response);
    }
  });
  return responsesToBeJumpped;
}

// TODO: rename this function to something better
/**
 * If a question was changed that affects the checkpoint due date then we should delete all responses after the checkpoint
 */
function pruneStepsBasedOnCheckpoints<
  Q extends Question<Option<C>, C>,
  C extends Checkpoint,
  R extends Response
>(validSteps: Step<Q, C, R>[], lastPhase: Phase): Step<Q, C, R>[] {
  for (let i = 0; i < validSteps.length; i++) {
    const checkpointStep = validSteps[i];
    if (checkpointStep.checkpoint?.reminderBaseDateQuestionId) {
      // if a step has a checkpoint and it has a reminderBaseDateQuestionId then we should
      //    find the question that the reminder base date is based off of
      //    and check to see if the question response that the reminder base date has changed
      for (const questionStep of validSteps) {
        const isReminderBaseDateForCheckpointStep =
          questionStep.question?.id ===
          checkpointStep.checkpoint.reminderBaseDateQuestionId;
        if (isReminderBaseDateForCheckpointStep) {
          const hasBaseDateQuestionBeenUpdated =
            questionStep.response &&
            questionStep.response.createdAt > lastPhase?.completedAt;
          if (hasBaseDateQuestionBeenUpdated) {
            // If the question that changes the checkpoint's reminder base date has been changed we should stop at that checkpoing again
            validSteps[i].stopAtCheckpoint = true;
            validSteps[i].response = null; // remove the response because it lies after the checkpoint
            return validSteps.slice(0, i + 1);
          }
        }
      }
    }
  }

  return validSteps;
}

export function getSurveyState<
  Q extends Question<Option<C>, C>,
  C extends Checkpoint,
  R extends Response
>(
  allQuestions: Q[],
  allQuestionResponses: R[],
  defaultFinalCheckpoint: C,
  phases: Phase[]
): {
  steps: Step<Q, C, R>[];
  validResponses: R[];
  checkpoint: C;
  responsesToBeJumpped: R[];
  responsesToBeDeleted: R[];
  lastQuestionAnswered: Q;
} {
  /**
   * Handle the empty case
   */
  if (!allQuestions?.length) {
    return {
      steps: [],
      validResponses: [],
      checkpoint: null,
      responsesToBeJumpped: [],
      responsesToBeDeleted: [],
      lastQuestionAnswered: null,
    };
  }

  /**
   * Create a map from questionId to response so we can look up responses quickly
   */
  const questionResponseMap = allQuestionResponses.reduce((acc, qr) => {
    acc[qr.questionId] = qr;
    return acc;
  }, {} as Record<number, R>);

  /**
   * Go through the chain of quesitons and build out the survey
   * start with the first question in sort order
   */
  let validSteps: Step<Q, C, R>[] = [];
  const questionsSeen: { [key: number]: true } = {};

  // the first step should be the first question in sort order
  let step: Step<Q, C, R> = {
    question: allQuestions[0],
    response: questionResponseMap[allQuestions[0].id],
    checkpoint: null,
  };

  while (step) {
    // If we're experiencing a cyclical nextQuesitonId situation we should stop because that must mean we've hit a checkpoint but completing a phase doesn't happen here
    if (questionsSeen[step?.question?.id]) {
      if (!step.checkpoint) {
        throw Error(
          'Cyclical back to a previous question without a checkpoint'
        );
      }
      step.response = null;
    } else if (step.question) {
      step.response = questionResponseMap[step.question.id] || null;
    }
    questionsSeen[step?.question?.id] = true;
    validSteps.push(step);

    step = getNextStepFromLastStep(step, allQuestions, defaultFinalCheckpoint);
  }

  const previousStep =
    validSteps.length >= 2 ? validSteps[validSteps.length - 2] : null;
  const lastStep = validSteps[validSteps.length - 1];
  const lastPhase = getLastPhase(phases);

  // Determine if we should stop at the last checkpoint
  lastStep.stopAtCheckpoint = false;
  if (lastStep.checkpoint) {
    if (!lastStep.question) {
      // If there isn't a question associated with step then we for sure have to stop at the checkpoint because it must be a final step
      lastStep.stopAtCheckpoint = true;
    } else if (lastStep.response) {
      // if there is already a response for this step's question then we don't need to stop at the checkpoint
      lastStep.stopAtCheckpoint = false;
    } else {
      // We know this if the previous question was answered after the last phase was completed
      const lastPhaseSentUsToThisStep =
        previousStep &&
        lastPhase &&
        previousStep.response?.createdAt < lastPhase.completedAt;

      // const checkpointIsTheSameAsTheLastPhaseCheckpoint =
      //   lastStep.checkpoint?.id === lastPhase?.checkpoint?.id;

      lastStep.stopAtCheckpoint = !lastPhaseSentUsToThisStep; // && !checkpointIsTheSameAsTheLastPhaseCheckpoint;
    }
  }

  validSteps = pruneStepsBasedOnCheckpoints(validSteps, lastPhase);

  const { invalid: responsesToBeDeleted, valid: validResponses } =
    getValidAndInvalidResponses(validSteps, allQuestionResponses);

  return {
    steps: validSteps,
    checkpoint: lastStep.checkpoint,
    validResponses,
    responsesToBeJumpped: getJumpedResponses(validSteps),
    responsesToBeDeleted: responsesToBeDeleted,
    lastQuestionAnswered: getLastQuestionAnswered(validSteps),
  };
}

function getLastPhase(phases: Phase[]): Phase {
  const sortedCompltedPhases = (phases || [])
    .filter(p => p.completedAt)
    .sort((a, b) => b.completedAt - a.completedAt);
  return sortedCompltedPhases.length > 0 ? sortedCompltedPhases[0] : null;
}
